import React, { useState, createElement } from 'react';
import { SearchIcon, FilterIcon, DownloadIcon, PackageIcon, TrendingUpIcon, TrendingDownIcon, ArrowRightIcon } from 'lucide-react';
interface InventoryItem {
  id: string;
  reference: string;
  design: string;
  category: string;
  currentStock: number;
  minStock: number;
  maxStock: number;
  unitCost: number;
  totalValue: number;
  lastMovement: string;
  status: 'normal' | 'low' | 'critical' | 'excess';
}
const mockInventory: InventoryItem[] = [{
  id: '1',
  reference: 'PVM-6-5/8-GALV-001',
  design: '6" 5/8 GALV',
  category: 'Pivot Machine',
  currentStock: 65,
  minStock: 20,
  maxStock: 100,
  unitCost: 15000,
  totalValue: 975000,
  lastMovement: '2024-01-15',
  status: 'normal'
}, {
  id: '2',
  reference: 'PVM-5-GALV-002',
  design: '5" GALV',
  category: 'Pivot Machine',
  currentStock: 2,
  minStock: 20,
  maxStock: 50,
  unitCost: 12000,
  totalValue: 24000,
  lastMovement: '2024-01-10',
  status: 'critical'
}, {
  id: '3',
  reference: 'PVM-8-5/8-GALV-003',
  design: '8" 5/8 GALV',
  category: 'Pivot Machine',
  currentStock: 8,
  minStock: 15,
  maxStock: 30,
  unitCost: 18000,
  totalValue: 144000,
  lastMovement: '2024-01-12',
  status: 'low'
}, {
  id: '4',
  reference: 'PVM-HP-STD-004',
  design: 'High Profile',
  category: 'Pivot Machine',
  currentStock: 22,
  minStock: 10,
  maxStock: 20,
  unitCost: 12500,
  totalValue: 275000,
  lastMovement: '2024-01-18',
  status: 'excess'
}, {
  id: '5',
  reference: 'LIN-STD-005',
  design: 'Linear',
  category: 'Linear',
  currentStock: 1,
  minStock: 15,
  maxStock: 25,
  unitCost: 20000,
  totalValue: 20000,
  lastMovement: '2024-01-08',
  status: 'critical'
}, {
  id: '6',
  reference: 'PVM-6-5/8-POLY-006',
  design: '6" 5/8 POLY',
  category: 'Pivot Machine',
  currentStock: 5,
  minStock: 10,
  maxStock: 25,
  unitCost: 14000,
  totalValue: 70000,
  lastMovement: '2024-01-14',
  status: 'low'
}];
export function InventoryPanel() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'critical':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'low':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'excess':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'normal':
        return 'bg-green-100 text-green-700 border-green-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'critical':
      case 'low':
        return <TrendingDownIcon className="w-4 h-4" data-id="element-277" />;
      case 'excess':
        return <TrendingUpIcon className="w-4 h-4" data-id="element-278" />;
      default:
        return <PackageIcon className="w-4 h-4" data-id="element-279" />;
    }
  };
  const totalValue = mockInventory.reduce((sum, item) => sum + item.totalValue, 0);
  const totalItems = mockInventory.reduce((sum, item) => sum + item.currentStock, 0);
  const criticalItems = mockInventory.filter(item => item.status === 'critical').length;
  const lowStockItems = mockInventory.filter(item => item.status === 'low').length;
  const handleDownloadExcel = () => {
    const headers = ['Reference', 'Design', 'Category', 'Current Stock', 'Min Stock', 'Max Stock', 'Unit Cost', 'Total Value', 'Status', 'Last Movement'];
    const rows = mockInventory.map(item => [item.reference, item.design, item.category, item.currentStock.toString(), item.minStock.toString(), item.maxStock.toString(), `$${item.unitCost.toLocaleString()}`, `$${item.totalValue.toLocaleString()}`, item.status.toUpperCase(), item.lastMovement]);
    const csvContent = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };
  return <div className="space-y-6" data-id="element-280">
      <div className="flex items-center justify-between" data-id="element-281">
        <div data-id="element-282">
          <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-283">
            Inventario Completo
          </h2>
          <p className="text-gray-600 text-sm" data-id="element-284">
            Gestión detallada de referencias en almacén
          </p>
        </div>
        <button onClick={handleDownloadExcel} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg shadow-blue-500/30" data-id="element-285">
          <DownloadIcon className="w-4 h-4" data-id="element-286" />
          Exportar Excel
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-287">
        <div className="bg-white rounded-lg p-5 border border-gray-200" data-id="element-288">
          <div className="flex items-center justify-between mb-3" data-id="element-289">
            <PackageIcon className="w-6 h-6 text-blue-500" data-id="element-290" />
          </div>
          <p className="text-sm text-gray-600 mb-1" data-id="element-291">Total Unidades</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-292">{totalItems}</p>
        </div>
        <div className="bg-white rounded-lg p-5 border border-gray-200" data-id="element-293">
          <div className="flex items-center justify-between mb-3" data-id="element-294">
            <PackageIcon className="w-6 h-6 text-green-500" data-id="element-295" />
          </div>
          <p className="text-sm text-gray-600 mb-1" data-id="element-296">Valor Total</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-297">
            ${(totalValue / 1000000).toFixed(2)}M
          </p>
        </div>
        <div className="bg-red-50 rounded-lg p-5 border border-red-200" data-id="element-298">
          <div className="flex items-center justify-between mb-3" data-id="element-299">
            <TrendingDownIcon className="w-6 h-6 text-red-600" data-id="element-300" />
          </div>
          <p className="text-sm text-red-700 mb-1" data-id="element-301">Stock Crítico</p>
          <p className="text-3xl font-bold text-red-600" data-id="element-302">{criticalItems}</p>
        </div>
        <div className="bg-yellow-50 rounded-lg p-5 border border-yellow-200" data-id="element-303">
          <div className="flex items-center justify-between mb-3" data-id="element-304">
            <TrendingDownIcon className="w-6 h-6 text-yellow-600" data-id="element-305" />
          </div>
          <p className="text-sm text-yellow-700 mb-1" data-id="element-306">Stock Bajo</p>
          <p className="text-3xl font-bold text-yellow-600" data-id="element-307">{lowStockItems}</p>
        </div>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-308">
        <div className="p-6 border-b border-gray-200" data-id="element-309">
          <div className="flex items-center gap-4" data-id="element-310">
            <div className="flex-1 relative" data-id="element-311">
              <SearchIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-312" />
              <input type="text" placeholder="Buscar por referencia o diseño..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white text-gray-900 pl-12 pr-4 py-3 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all" data-id="element-313" />
            </div>
            <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="px-4 py-3 bg-white text-gray-700 rounded-lg border border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 focus:outline-none transition-all" data-id="element-314">
              <option value="all" data-id="element-315">Todas las categorías</option>
              <option value="pivot" data-id="element-316">Pivot Machine</option>
              <option value="linear" data-id="element-317">Linear</option>
            </select>
            <button className="flex items-center gap-2 px-5 py-3 bg-white text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors" data-id="element-318">
              <FilterIcon className="w-5 h-5" data-id="element-319" />
              Filtros
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-320">
          <table className="w-full" data-id="element-321">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-322">
              <tr data-id="element-323">
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-324">
                  Referencia
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-325">
                  Diseño
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-326">
                  Categoría
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-327">
                  Stock Actual
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-328">
                  Min / Max
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-329">
                  Valor Total
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-330">
                  Estado
                </th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-331">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-332">
              {mockInventory.map(item => <tr key={item.id} className="hover:bg-gray-50 transition-colors" data-id="element-333">
                  <td className="px-6 py-4" data-id="element-334">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-335">
                      {item.reference}
                    </p>
                  </td>
                  <td className="px-6 py-4" data-id="element-336">
                    <p className="text-sm text-gray-900" data-id="element-337">{item.design}</p>
                  </td>
                  <td className="px-6 py-4" data-id="element-338">
                    <span className="inline-flex items-center px-2.5 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200" data-id="element-339">
                      {item.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center" data-id="element-340">
                    <p className="text-sm font-bold text-gray-900" data-id="element-341">
                      {item.currentStock}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-center" data-id="element-342">
                    <p className="text-sm text-gray-600" data-id="element-343">
                      {item.minStock} / {item.maxStock}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-344">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-345">
                      ${item.totalValue.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-346">
                      ${item.unitCost.toLocaleString()} / unit
                    </p>
                  </td>
                  <td className="px-6 py-4" data-id="element-347">
                    <div className="flex justify-center" data-id="element-348">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold border ${getStatusColor(item.status)}`} data-id="element-349">
                        {getStatusIcon(item.status)}
                        {item.status.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-350">
                    <div className="flex justify-center" data-id="element-351">
                      <button className="flex items-center gap-1 px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors text-sm font-medium" data-id="element-352">
                        Ver
                        <ArrowRightIcon className="w-4 h-4" data-id="element-353" />
                      </button>
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}